import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'forest.subparcels.layer';

/**
 * Whether the subpolygon overlay is on, shared by every map
 * surface in the tab.
 *
 * A module-level store rather than per-component state, for the same
 * reason `useIcasToggles` is one: the checkbox lives inside Leaflet's
 * own layers widget on one map while the layer renders on another, and
 * the `storage` event only fires cross-tab. `useSyncExternalStore` makes
 * this a single source of truth without a Context provider.
 *
 * Defaults to ON: subpolygons are meant to be seen on top of their
 * parcels, and the layer is small.
 */
const DEFAULT_ON = true;

function readStored(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_ON;
    return raw === 'true';
  } catch {
    return DEFAULT_ON;
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();
let snapshot = readStored();

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    snapshot = readStored();
    listeners.forEach((l) => l());
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): boolean {
  return snapshot;
}

export function setSubParcelsLayerEnabled(next: boolean): void {
  if (snapshot === next) return;
  snapshot = next;
  try {
    localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // Private-mode storage failures must not break the toggle.
  }
  listeners.forEach((l) => l());
}

export function useSubParcelsLayerEnabled(): {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
} {
  const enabled = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_ON);
  const setEnabled = useCallback((next: boolean) => setSubParcelsLayerEnabled(next), []);
  return { enabled, setEnabled };
}
